import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPageLayout from './admin/AdminPageLayout';
import { GoogleMap, useLoadScript, Marker, InfoWindow, Circle, OverlayView, MarkerClusterer } from '@react-google-maps/api';
import axios from 'axios';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  ListItemText as MUIListItemText,
  ListItemAvatar,
  Avatar,
  Card,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight, 
  Today, 
  Search as SearchIcon, 
  Clear as ClearIcon,
  Directions as DirectionsIcon,
  ExpandMore,
  ExpandLess,
  Visibility,
  VisibilityOff,
  PersonAdd
} from '@mui/icons-material';
import jobService from '../services/jobService';
import logger from '../utils/logger';
import googleMapsCache from '../services/googleMapsCache';
import customerService from '../services/customerService';
import ClientOutreachDialog from './admin/ClientOutreachDialog';

const APPBAR_HEIGHT = 64;

// Map container styles - right side only
const containerStyle = {
  width: '100%',
  height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
};

// Default map options
const defaultMapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

// Predefined city centers for common REACT_APP_CITY_CODE values
// In a real application, these would ideally come from a backend endpoint
// or a more robust configuration.
const cityCenters = {
  'MCO': { lat: 28.5383, lng: -81.3792 }, // Orlando, FL
  'NYC': { lat: 40.7128, lng: -74.0060 }, // New York City, NY
  'LAX': { lat: 34.0522, lng: -118.2437 }, // Los Angeles, CA
  'ORL': { lat: 28.5383, lng: -81.3792 }, // Orlando, FL (added based on previous discussion)
  'KIS': { lat: 28.2920, lng: -81.4076 }, // Kissimmee, FL (added based on previous discussion)
  // Add more as needed
};

// Date utility functions for weekly navigation
const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is sunday
  return new Date(d.setDate(diff));
};

const getWeekEnd = (weekStart) => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
};

const formatWeekDisplay = (weekStart) => {
  const options = { month: 'long', day: 'numeric', year: 'numeric' };
  return `Week of ${weekStart.toLocaleDateString('en-US', options)}`;
};

const navigateWeek = (currentWeek, direction) => {
  const newWeek = new Date(currentWeek);
  newWeek.setDate(currentWeek.getDate() + (direction * 7));
  return newWeek;
};

const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0] + 'T00:00:00Z';
};

const formatDateEndForAPI = (date) => {
  return date.toISOString().split('T')[0] + 'T23:59:59Z';
};

// Calculate job circle size based on total amount
const calculateJobCircleSize = (totalAmount) => {
  if (!totalAmount || totalAmount <= 0) return 8; // Minimum size for jobs with no amount
  
  // Convert from cents to dollars
  const dollarAmount = totalAmount / 100;
  
  // Define size ranges based on job value
  if (dollarAmount < 100) return 8;        // Small jobs: $0-99
  if (dollarAmount < 200) return 12;       // Medium jobs: $100-199
  if (dollarAmount < 300) return 16;       // Large jobs: $200-299
  if (dollarAmount < 500) return 20;       // XL jobs: $300-499
  return 24;                               // XXL jobs: $500+
};

// Get maid pin color based on onboarding status
const getMaidPinColor = (onboardingStatus) => {
  const statusColors = {
    'approved': '#F44336',      // Red for approved
    'pending_review': '#FF9800', // Orange for pending review
    'incomplete': '#9E9E9E',    // Gray for incomplete
    'rejected': '#424242'       // Dark gray for rejected
  };
  
  return statusColors[onboardingStatus] || statusColors['incomplete'];
};

const MaidMapDisplay = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'], // Required for GeocodingService
  });

  const [mapCenter, setMapCenter] = useState(null);
  const [maids, setMaids] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMaids, setShowMaids] = useState(true);
  const [showJobs, setShowJobs] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => getWeekStart());
  const [jobsLoading, setJobsLoading] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchedLocation, setSearchedLocation] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [maidStatusFilter, setMaidStatusFilter] = useState(['approved', 'pending_review', 'incomplete', 'rejected']);
  const [jobStatusFilter, setJobStatusFilter] = useState(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']);
  const [suppliesFilter, setSuppliesFilter] = useState(false);
  const [availableDaysFilter, setAvailableDaysFilter] = useState([]);
  const [maidDistances, setMaidDistances] = useState({});
  const [calculatingDistances, setCalculatingDistances] = useState(false);
  const [maxDistance, setMaxDistance] = useState(25); // Default 25 miles
  const [sortBy, setSortBy] = useState('distance');
  const [showDistancePanel, setShowDistancePanel] = useState(true);
  const [showDistanceRadius, setShowDistanceRadius] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [maidsLoaded, setMaidsLoaded] = useState(false); // Track if maids have been loaded
  const [maidsFilterMode, setMaidsFilterMode] = useState(null); // Track filter mode: null, 'all', or 'address'
  const [lastActiveDays, setLastActiveDays] = useState(null); // Filter maids by last login within X days

  // Assignment state
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedMaidForAssignment, setSelectedMaidForAssignment] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignmentPayout, setAssignmentPayout] = useState(''); // required payout in dollars

  // Fake customer creation state
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerCreationResult, setCustomerCreationResult] = useState(null);

  // Customer layer state (parallels maids/jobs)
  const [customers, setCustomers] = useState([]);
  const [showCustomers, setShowCustomers] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [customerStatusFilter, setCustomerStatusFilter] = useState(['active', 'inactive']);

  // Outreach dialog opened from a customer pin
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [outreachInitialEmails, setOutreachInitialEmails] = useState(null);

  const navigate = useNavigate();
  const mapRef = useRef(null);
  const geocoderRef = useRef(null); // Ref for Google Maps Geocoder service

  // Initialize Geocoder service once Google Maps script is loaded
  useEffect(() => {
    if (isLoaded && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // Haversine formula to calculate distance between two points on Earth
  const calculateHaversineDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // Distance in kilometers
    return distance * 0.621371; // Convert to miles
  }, []);

  // Estimate driving time based on distance (rough approximation)
  const estimateDrivingTime = useCallback((distanceInMiles) => {
    // Assume average speed of 25-30 mph in urban areas
    const avgSpeed = 27; // mph
    const timeInHours = distanceInMiles / avgSpeed;
    const timeInMinutes = Math.round(timeInHours * 60);
    
    if (timeInMinutes < 60) {
      return `${timeInMinutes} min`;
    } else {
      const hours = Math.floor(timeInMinutes / 60);
      const minutes = timeInMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }, []);

  // Calculate distances and travel times from searched location to maids with caching
  const calculateDistancesToMaids = useCallback(async (searchLocation, maidsToCalculate) => {
    if (!searchLocation || !maidsToCalculate || maidsToCalculate.length === 0) {
      return;
    }

    setCalculatingDistances(true);

    try {
      const maidDestinations = maidsToCalculate.map(maid => maid.location).filter(Boolean);
      
      // Check cache first for the entire distance matrix request
      const cachedDistanceMatrix = googleMapsCache.getCachedDistance(searchLocation, maidDestinations);
      if (cachedDistanceMatrix) {
        logger.log('Using cached distance matrix results');
        setMaidDistances(cachedDistanceMatrix);
        setCalculatingDistances(false);
        return;
      }

      // If not fully cached, proceed with API calls
      if (isLoaded && window.google?.maps?.DistanceMatrixService) {
        const distanceService = new window.google.maps.DistanceMatrixService();
        
        try {
          const response = await new Promise((resolve, reject) => {
            distanceService.getDistanceMatrix({
              origins: [searchLocation],
              destinations: maidDestinations,
              travelMode: window.google.maps.TravelMode.DRIVING,
              unitSystem: window.google.maps.UnitSystem.IMPERIAL,
              avoidHighways: false,
              avoidTolls: false
            }, (response, status) => {
              if (status === 'OK') {
                resolve(response);
              } else {
                reject(new Error(`Distance Matrix request failed: ${status}`));
              }
            });
          });

          // Process Google Maps results and build distance data
          const distances = {};
          const elements = response.rows[0].elements;
          
          maidsToCalculate.forEach((maid, index) => {
            const element = elements[index];
            if (element.status === 'OK') {
              distances[maid.id] = {
                distance: element.distance.text,
                duration: element.duration.text,
                distanceValue: element.distance.value, // meters
                durationValue: element.duration.value  // seconds
              };
            } else {
              // Fallback to Haversine calculation for failed elements
              const distanceInMiles = calculateHaversineDistance(
                searchLocation.lat, searchLocation.lng,
                maid.location.lat, maid.location.lng
              );
              distances[maid.id] = {
                distance: `${distanceInMiles.toFixed(1)} mi`,
                duration: estimateDrivingTime(distanceInMiles),
                distanceValue: distanceInMiles * 1609.34, // Convert to meters
                durationValue: null // No duration value for Haversine
              };
            }
          });

          // Cache the complete distance matrix result
          googleMapsCache.cacheDistance(searchLocation, maidDestinations, distances);
          logger.log('Distance calculations completed and cached via Google Maps API:', distances);
          
          setMaidDistances(distances);
          return;
        } catch (apiError) {
          logger.log('Google Maps Distance Matrix API failed, falling back to Haversine:', apiError);
          // Fall through to Haversine calculation
        }
      }

      // Fallback: Use Haversine formula for direct distance calculation
      logger.log('Using Haversine formula for distance calculation');
      const distances = {};
      
      maidsToCalculate.forEach((maid) => {
        const distanceInMiles = calculateHaversineDistance(
          searchLocation.lat, searchLocation.lng,
          maid.location.lat, maid.location.lng
        );
        
        distances[maid.id] = {
          distance: `${distanceInMiles.toFixed(1)} mi`,
          duration: estimateDrivingTime(distanceInMiles),
          distanceValue: distanceInMiles * 1609.34, // Convert to meters for consistency
          durationValue: null // No precise duration for Haversine calculation
        };
      });

      // Cache Haversine results as well (with shorter TTL)
      googleMapsCache.cacheDistance(searchLocation, maidDestinations, distances);
      setMaidDistances(distances);
      logger.log('Distance calculations completed via Haversine formula and cached:', distances);
      
    } catch (error) {
      logger.log('Error calculating distances:', error);
      setSearchError('Unable to calculate distances. Please try again.');
    } finally {
      setCalculatingDistances(false);
    }
  }, [isLoaded, calculateHaversineDistance, estimateDrivingTime]);


  // Function to geocode an address with caching
  const geocodeAddress = useCallback(async (address) => {
    if (!address || typeof address !== 'string') {
      logger.log('Invalid address provided for geocoding');
      return null;
    }

    // Smart invalid address detection (final safety check)
    const isInvalidAddress = (addr) => {
      const trimmed = addr.trim().toLowerCase();

      if (trimmed.length < 10) return true;
      if (!/\d/.test(trimmed)) return true; // No numbers
      if (/(.)\1{3,}/.test(trimmed)) return true; // Repeated chars

      // Keyboard mashing detection - DISABLED to avoid false positives with legitimate addresses like "Ritz"
      // const qwertySequences = ['qwe', 'wer', 'ert', 'rty', 'tyu', 'yui', 'uio', 'iop', 'asd', 'sdf', 'dfg', 'fgh', 'ghj', 'hjk', 'jkl', 'zxc', 'xcv', 'cvb', 'vbn', 'bnm'];
      // if (qwertySequences.some(seq => trimmed.includes(seq))) return true;

      // Test patterns
      const testPatterns = [/^test/i, /^demo/i, /^qsd/i, /^sqdq/i, /^erze/i, /placeholder/i, /my.*address/i, /residential.*address/i];
      if (testPatterns.some(pattern => pattern.test(trimmed))) return true;

      return false;
    };
    
    if (isInvalidAddress(address)) {
      logger.log(`🚨 BLOCKED: Smart filter refusing to geocode invalid address: "${address}"`);
      return null;
    }

    // Check cache first
    const cachedResult = googleMapsCache.getCachedGeocode(address);
    if (cachedResult) {
      logger.log(`Using cached geocoding result for: ${address}`);
      return cachedResult;
    }

    // If not in cache, use Google Geocoding API
    if (!geocoderRef.current) {
      logger.log('Geocoder not initialized yet.');
      return null;
    }

    logger.log(`🌐 Making Google Maps API call for: "${address}"`);
    return new Promise((resolve) => {
      geocoderRef.current.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };
          
          // Cache the successful result
          googleMapsCache.cacheGeocode(address, location);
          logger.log(`Geocoded and cached address: ${address} -> ${location.lat}, ${location.lng}`);
          resolve(location);
        } else {
          logger.log(`Geocoding failed for "${address}": ${status}`);
          resolve(null);
        }
      });
    });
  }, []);

  // Function to fetch maids and their geocoded locations
  const fetchMaidsAndLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

      // Fetch ALL active maids from backend using lightweight endpoint
      // For map display, we need all active maids, so fetch multiple pages if needed
      let allMaids = [];
      let page = 1;
      let hasMorePages = true;
      const pageSize = 200; // Reasonable page size for map display

      console.log('🗺️ Map: Starting to fetch all active maids...');

      while (hasMorePages) {
        console.log(`🗺️ Map: Fetching page ${page} with limit ${pageSize}`);
        
        const response = await axios.get(`${apiBase}/api/maids/light`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            activeOnly: 'true',
            limit: pageSize,
            page: page
          }
        });

        const pageData = response.data?.data || [];
        const pagination = response.data?.pagination || {};
        
        console.log(`🗺️ Map: Page ${page} returned ${pageData.length} maids`);
        console.log(`🗺️ Map: Pagination info:`, pagination);
        
        allMaids = [...allMaids, ...pageData];
        
        // More robust pagination check
        if (pageData.length === 0) {
          console.log('🗺️ Map: No more data, stopping pagination');
          hasMorePages = false;
        } else if (pagination.totalPages && page >= pagination.totalPages) {
          console.log(`🗺️ Map: Reached last page ${page}/${pagination.totalPages}`);
          hasMorePages = false;
        } else if (pagination.hasNext === false) {
          console.log('🗺️ Map: API indicates no more pages');
          hasMorePages = false;
        } else if (pageData.length < pageSize) {
          console.log(`🗺️ Map: Partial page (${pageData.length} < ${pageSize}), assuming last page`);
          hasMorePages = false;
        } else {
          console.log(`🗺️ Map: More pages available, continuing...`);
          hasMorePages = true;
        }
        
        page++;

        // Safety break to prevent infinite loops
        if (page > 20) {
          console.warn('🗺️ Map: Safety break - stopped fetching after 20 pages (4000 maids)');
          break;
        }
      }

      const fetchedMaids = allMaids;
      console.log(`🗺️ Map: Final result - loaded ${fetchedMaids.length} active maids for map display`);
      logger.log(`Map: Loaded ${fetchedMaids.length} active maids for display`);

      // OPTIMIZATION: Separate maids with stored coordinates from those needing geocoding
      const maidsWithStoredCoords = fetchedMaids.filter(maid =>
        maid.latitude && maid.longitude &&
        typeof maid.latitude === 'number' && typeof maid.longitude === 'number'
      );

      const maidsNeedingGeocoding = fetchedMaids.filter(maid =>
        !maid.latitude || !maid.longitude ||
        typeof maid.latitude !== 'number' || typeof maid.longitude !== 'number'
      );

      logger.log(`🚀 Performance Optimization: ${maidsWithStoredCoords.length} maids have stored coordinates, ${maidsNeedingGeocoding.length} need geocoding`);

      // Process maids with stored coordinates directly (no API calls needed)
      const maidsWithGeocodedFromDB = maidsWithStoredCoords.map(maid => ({
        ...maid,
        location: {
          lat: parseFloat(maid.latitude),
          lng: parseFloat(maid.longitude)
        },
        type: 'maid'
      }));

      logger.log(`✅ Processed ${maidsWithGeocodedFromDB.length} maids with stored coordinates (no geocoding needed)`);

      // Batch geocoding optimization: check cache for all addresses first
      // Filter out invalid/test addresses that shouldn't be geocoded
      const validMaidAddresses = maidsNeedingGeocoding
        .filter(maid => {
          const address = maid.residentialAddress;
          if (!address || typeof address !== 'string') return false;
          
          // Smart heuristic-based invalid address detection
          const isInvalidAddress = (addr) => {
            const trimmed = addr.trim().toLowerCase();

            // Too short to be a real address
            if (trimmed.length < 10) return true;

            // Contains no numbers (real addresses usually have street numbers)
            if (!/\d/.test(trimmed)) return true;

            // Contains excessive repeated characters (like "aaaa" or "qqqq")
            if (/(.)\1{3,}/.test(trimmed)) return true;

            // Keyboard mashing detection - DISABLED to avoid false positives with legitimate addresses like "Ritz"
            // const qwertySequences = ['qwe', 'wer', 'ert', 'rty', 'tyu', 'yui', 'uio', 'iop', 'asd', 'sdf', 'dfg', 'fgh', 'ghj', 'hjk', 'jkl', 'zxc', 'xcv', 'cvb', 'vbn', 'bnm'];
            // if (qwertySequences.some(seq => trimmed.includes(seq))) return true;
            
            // Common test patterns
            const testPatterns = [
              /^test/i, /^demo/i, /^sample/i, /^fake/i, /^temp/i,
              /^qsd/i, /^sqdq/i, /^abc/i, /^xxx/i, /^erze/i,
              /placeholder/i, /example/i, /^n\/a$/i, /^none$/i, /^null$/i,
              /my.*address/i, /residential.*address/i, /^address/i, /^street/i,
              /^home$/i, /^house$/i, /^\d+$/
            ];
            
            if (testPatterns.some(pattern => pattern.test(trimmed))) return true;
            
            // Must contain at least one realistic word (street, avenue, drive, etc.)
            const streetWords = ['street', 'st', 'avenue', 'ave', 'road', 'rd', 'drive', 'dr', 'lane', 'ln', 'way', 'blvd', 'boulevard', 'place', 'pl', 'court', 'ct', 'circle', 'cir'];
            const hasStreetWord = streetWords.some(word => trimmed.includes(word));
            
            // Must have reasonable structure (letters and numbers, not just gibberish)
            const hasLettersAndNumbers = /[a-z]/.test(trimmed) && /\d/.test(trimmed);
            
            // If it has no street words and no proper structure, it's probably fake
            if (!hasStreetWord && !hasLettersAndNumbers) return true;
            
            return false;
          };
          
          if (isInvalidAddress(address)) {
            logger.log(`🚫 Smart filter blocked invalid address for maid ${maid.id}: "${address}"`);
            return false;
          }
          
          return true;
        });
      
      const maidAddresses = validMaidAddresses.map(maid => ({ maid, address: maid.residentialAddress }));
      
      logger.log(`Checking cache for ${maidAddresses.length} maid addresses`);
      
      // Check cache for all addresses with detailed logging
      const cachedResults = new Map();
      const uncachedAddresses = [];
      
      maidAddresses.forEach(({ maid, address }) => {
        logger.log(`🔍 Checking cache for maid ${maid.id} address: "${address}"`);
        const cachedResult = googleMapsCache.getCachedGeocode(address);
        if (cachedResult) {
          cachedResults.set(maid.id, cachedResult);
          logger.log(`✅ Cache HIT for maid ${maid.id} address: ${address}`, cachedResult);
        } else {
          uncachedAddresses.push({ maid, address });
          logger.log(`❌ Cache MISS for maid ${maid.id} address: ${address}`);
        }
      });
      
      logger.log(`Cache hits: ${cachedResults.size}, API calls needed: ${uncachedAddresses.length}`);
      
      // For uncached addresses, use batch geocoding with a single API call
      const uncachedResults = new Map();
      if (uncachedAddresses.length > 0) {
        logger.log(`Making batch geocoding API call for ${uncachedAddresses.length} uncached maid addresses`);
        
        // Process uncached addresses in small batches to avoid overwhelming the API
        const batchSize = Math.min(5, uncachedAddresses.length);
        for (let i = 0; i < uncachedAddresses.length; i += batchSize) {
          const batch = uncachedAddresses.slice(i, i + batchSize);
          
          for (const { maid, address } of batch) {
            try {
              const location = await geocodeAddress(address);
              if (location) {
                uncachedResults.set(maid.id, location);
              }
            } catch (error) {
              logger.log(`Failed to geocode maid ${maid.id} address: ${address}`);
            }
          }
          
          // Delay between batches only
          if (i + batchSize < uncachedAddresses.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      // Combine results and build final maid list
      // Process maids that needed geocoding
      const maidsWithNewlyGeocodedLocations = maidsNeedingGeocoding.map(maid => {
        if (!maid.residentialAddress) {
          return { ...maid, location: null, type: 'maid' };
        }

        const location = cachedResults.get(maid.id) || uncachedResults.get(maid.id);
        return { ...maid, location: location || null, type: 'maid' };
      });

      // Combine maids with stored coordinates + newly geocoded maids
      const allMaidsWithLocations = [
        ...maidsWithGeocodedFromDB,  // Already have locations from DB
        ...maidsWithNewlyGeocodedLocations  // Newly geocoded or failed
      ];

      logger.log(`📍 Final result: ${allMaidsWithLocations.filter(m => m.location).length} maids with valid locations out of ${allMaidsWithLocations.length} total`);

      setMaids(allMaidsWithLocations.filter(maid => maid.location)); // Only keep maids with valid locations
    } catch (err) {
      logger.log('Error fetching or geocoding maid data:', err);
      throw new Error('Failed to load technician locations');
    }
  }, [geocodeAddress]);

  // Function to fetch jobs and their geocoded locations
  const fetchJobsAndLocations = useCallback(async (weekStart = selectedWeekStart) => {
    try {
      setJobsLoading(true);
      const weekEnd = getWeekEnd(weekStart);
      
      logger.log(`Fetching jobs for week: ${formatDateForAPI(weekStart)} to ${formatDateEndForAPI(weekEnd)}`);
      
      // Fetch jobs with booking data for the selected week
      const fetchedJobs = await jobService.fetchJobsForMapping({
        scheduledStartMin: formatDateForAPI(weekStart),
        scheduledStartMax: formatDateEndForAPI(weekEnd)
      });

      logger.log('Fetched jobs from API:', fetchedJobs);
      logger.log('Fetched jobs structure:', {
        totalJobs: fetchedJobs.length,
        sampleJob: fetchedJobs[0],
        jobIds: fetchedJobs.map(j => j.id),
        jobsWithAddresses: fetchedJobs.filter(j => j.address).length,
        jobsWithoutAddresses: fetchedJobs.filter(j => !j.address).length,
        jobsWithBooking: fetchedJobs.filter(j => j.booking).length,
        jobsWithCustomer: fetchedJobs.filter(j => j.customer).length,
        jobsWithHcpRawData: fetchedJobs.filter(j => j.hcpRawData).length
      });

      // Batch geocoding optimization for jobs: check cache for all addresses first
      const jobAddresses = fetchedJobs
        .map(job => ({ job, address: jobService.extractJobAddress(job) }))
        .filter(({ address }) => address);
      
      logger.log(`Checking cache for ${jobAddresses.length} job addresses`);
      logger.log('Job address extraction results:', fetchedJobs.map(job => ({
        jobId: job.id,
        hasAddress: !!job.address,
        addressType: typeof job.address,
        addressValue: job.address,
        extractedAddress: jobService.extractJobAddress(job)
      })));
      
      // Check cache for all job addresses with detailed logging
      const jobCachedResults = new Map();
      const jobUncachedAddresses = [];
      
      jobAddresses.forEach(({ job, address }) => {
        logger.log(`🔍 Checking cache for job ${job.id} address: "${address}"`);
        const cachedResult = googleMapsCache.getCachedGeocode(address);
        if (cachedResult) {
          jobCachedResults.set(job.id, cachedResult);
          logger.log(`✅ Cache HIT for job ${job.id} address: ${address}`, cachedResult);
        } else {
          jobUncachedAddresses.push({ job, address });
          logger.log(`❌ Cache MISS for job ${job.id} address: ${address}`);
        }
      });
      
      logger.log(`Job cache hits: ${jobCachedResults.size}, API calls needed: ${jobUncachedAddresses.length}`);
      
      // For uncached job addresses, use batch geocoding
      const jobUncachedResults = new Map();
      if (jobUncachedAddresses.length > 0) {
        logger.log(`Making batch geocoding API call for ${jobUncachedAddresses.length} uncached job addresses`);
        
        // Process uncached job addresses in small batches
        const batchSize = Math.min(5, jobUncachedAddresses.length);
        for (let i = 0; i < jobUncachedAddresses.length; i += batchSize) {
          const batch = jobUncachedAddresses.slice(i, i + batchSize);
          
          for (const { job, address } of batch) {
            try {
              logger.log(`Job ${job.id} address:`, address);
              const location = await geocodeAddress(address);
              if (location) {
                jobUncachedResults.set(job.id, location);
                logger.log(`Job ${job.id} geocoded to:`, location);
              }
            } catch (error) {
              logger.log(`Failed to geocode job ${job.id} address: ${address}`);
            }
          }
          
          // Delay between batches only
          if (i + batchSize < jobUncachedAddresses.length) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      // Build final job list with geocoded locations using the same mapping approach as ServiceSchedulePage
      const jobsWithGeocodedLocations = fetchedJobs.map(job => {
        // Extract and format address using the same logic as ServiceSchedulePage
        let formattedAddress = null;
        let customerName = 'Unknown Customer';
        let customerEmail = 'No email';
        let customerPhone = 'No phone';
        let serviceDescription = 'Unknown service';
        let serviceDate = 'Unknown date';
        let amount = 0;
        
        // Extract customer information from job.customer or fallback to HCP raw data (same as ServiceSchedulePage)
        customerName = job.customer?.customerName || 'Unknown Customer';
        customerEmail = job.customer?.customerEmail || null;
        customerPhone = job.customer?.customerPhone || null;
        
        // If no customer info in main field, try to extract from HCP raw data
        if (!customerName || customerName === 'Unknown Customer') {
          if (job.hcpRawData?.customer?.name) {
            customerName = job.hcpRawData.customer.name;
          } else if (job.hcpRawData?.customer?.first_name || job.hcpRawData?.customer?.last_name) {
            customerName = `${job.hcpRawData.customer.first_name || ''} ${job.hcpRawData.customer.last_name || ''}`.trim();
          }
        }
        
        if (!customerEmail && job.hcpRawData?.customer?.email) {
          customerEmail = job.hcpRawData.customer.email;
        }
        
        if (!customerPhone && job.hcpRawData?.customer?.phone) {
          customerPhone = job.hcpRawData.customer.phone;
        }
        
        // Extract service description (same as ServiceSchedulePage)
        if (job.jobType) {
          serviceDescription = job.jobType;
        } else if (job.hcpRawData?.service_description) {
          serviceDescription = job.hcpRawData.service_description;
        }
        
        // Extract service date - prioritize actual start time, fallback to scheduled
        let startDate;
        if (job.utcActualStartTime) {
          // Use actual start time if available (converted to local time)
          startDate = new Date(job.utcActualStartTime);
        } else if (job.utcScheduledDate) {
          startDate = new Date(job.utcScheduledDate);
        } else if (job.utcScheduledTime) {
          startDate = new Date(job.utcScheduledTime);
        } else if (job.scheduledStartTime) {
          startDate = new Date(job.scheduledStartTime);
        } else if (job.hcpRawData?.scheduled_start) {
          startDate = new Date(job.hcpRawData.scheduled_start);
        } else if (job.booking?.scheduledStartTime) {
          startDate = new Date(job.booking.scheduledStartTime);
        }
        
        if (startDate && !isNaN(startDate.getTime())) {
          serviceDate = startDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        }
        
        // Extract amount (same as ServiceSchedulePage)
        if (job.totalAmount) {
          amount = job.totalAmount;
        } else if (job.total_amount) {
          amount = job.total_amount;
        } else if (job.booking?.amount) {
          amount = job.booking.amount;
        }
        
        // Extract and format address (same as ServiceSchedulePage)
        logger.log(`Job ${job.id} address extraction:`, {
          hasJobAddress: !!job.address,
          hasHcpAddress: !!job.hcpRawData?.customer?.address,
          hasBookingAddress: !!job.booking?.address,
          jobAddressType: typeof job.address,
          hcpAddressType: typeof job.hcpRawData?.customer?.address,
          bookingAddressType: typeof job.booking?.address
        });
        
        if (job.address) {
          formattedAddress = formatJobAddress(job.address);
          logger.log(`Job ${job.id} using job.address:`, formattedAddress);
        } else if (job.hcpRawData?.customer?.address) {
          formattedAddress = formatJobAddress(job.hcpRawData.customer.address);
          logger.log(`Job ${job.id} using hcpRawData.customer.address:`, formattedAddress);
        } else if (job.booking?.address) {
          formattedAddress = formatJobAddress(job.booking.address);
          logger.log(`Job ${job.id} using booking.address:`, formattedAddress);
        }
        
        if (!formattedAddress) {
          logger.log(`Job ${job.id} has no valid address - skipping`);
          return { ...job, location: null, type: 'job' };
        }
        
        const location = jobCachedResults.get(job.id) || jobUncachedResults.get(job.id);
        if (!location) {
          logger.log(`Job ${job.id} could not be geocoded - skipping`);
          return { ...job, location: null, type: 'job' };
        }
        
        const circleSize = calculateJobCircleSize(amount);
        logger.log(`Job ${job.id}: $${(amount / 100).toFixed(2)} -> circle size ${circleSize}`);
        
        // Check if job has assigned maids (same logic as ServiceSchedulePage)
        const hasAssignments = job.maidAssignments && job.maidAssignments.allMaids && job.maidAssignments.allMaids.length > 0;
        const assignmentAwareColor = hasAssignments ? jobService.getJobStatusColor(job.status) : '#F44336'; // Red for unassigned
        
        return { 
          ...job, 
          location, 
          type: 'job',
          extractedAddress: formattedAddress,
          statusColor: assignmentAwareColor,
          hasAssignments: hasAssignments,
          circleSize: circleSize,
          // Add convenience fields for InfoWindow (same as ServiceSchedulePage)
          customerName: customerName,
          customerEmail: customerEmail,
          customerPhone: customerPhone,
          serviceDescription: serviceDescription,
          serviceDate: serviceDate,
          totalAmount: amount,
          assignedMaids: job.maidAssignments?.allMaids?.map(maid => 
            `${maid.firstName} ${maid.lastName}`
          ) || []
        };
      });

      const validJobs = jobsWithGeocodedLocations.filter(job => job.location);
      logger.log(`Successfully geocoded ${validJobs.length} out of ${fetchedJobs.length} jobs`);
      logger.log('Final jobs to be displayed:', validJobs.map(job => ({
        id: job.id,
        hasLocation: !!job.location,
        location: job.location,
        address: job.extractedAddress,
        customerName: job.customerName
      })));
      
      setJobs(validJobs);
      setJobsLoading(false);
    } catch (err) {
      logger.log('Error fetching or geocoding job data:', err);
      setJobsLoading(false);
      throw new Error('Failed to load job locations');
    }
  }, [geocodeAddress, selectedWeekStart]);

  // Fetch customers and their geocoded locations (mirrors fetchMaids/Jobs).
  // Uses stored ClientProfile coordinates when present, else geocodes the
  // address client-side (cache-aware, throttled) — coords are NOT persisted back.
  const fetchCustomersAndLocations = useCallback(async () => {
    try {
      setCustomersLoading(true);
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

      const response = await axios.get(`${apiBase}/api/users/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedCustomers = Array.isArray(response.data) ? response.data : [];
      logger.log(`Map: Loaded ${fetchedCustomers.length} customers for display`);

      // Build a full address string for a customer (null-guarded).
      const buildAddress = (c) => {
        const p = c.clientProfile || {};
        return [c.address || p.address, c.city || p.city, c.state || p.state, c.zipCode || p.zipCode]
          .filter(Boolean)
          .join(', ');
      };

      const readCoords = (c) => {
        const lat = parseFloat(c.latitude ?? c.clientProfile?.latitude);
        const lng = parseFloat(c.longitude ?? c.clientProfile?.longitude);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
      };

      // Stored-coords-first split (like maids): skip geocoding when lat/lng exist.
      const withStored = [];
      const needGeocoding = [];
      fetchedCustomers.forEach((c) => {
        const coords = readCoords(c);
        if (coords) {
          withStored.push({ ...c, location: coords, type: 'customer', extractedAddress: buildAddress(c) });
        } else {
          needGeocoding.push(c);
        }
      });
      logger.log(`Customers: ${withStored.length} with stored coords, ${needGeocoding.length} need geocoding`);

      // Geocode the rest in batches of 5 with a 500ms pause (cache-aware).
      const geocoded = [];
      const geoTargets = needGeocoding
        .map((c) => ({ c, address: buildAddress(c) }))
        .filter(({ address }) => address && address.length >= 5);

      const batchSize = 5;
      for (let i = 0; i < geoTargets.length; i += batchSize) {
        const batch = geoTargets.slice(i, i + batchSize);
        for (const { c, address } of batch) {
          try {
            const location = await geocodeAddress(address);
            if (location) {
              geocoded.push({ ...c, location, type: 'customer', extractedAddress: address });
            }
          } catch (e) {
            logger.log(`Failed to geocode customer ${c.id} address: ${address}`);
          }
        }
        if (i + batchSize < geoTargets.length) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      const allCustomers = [...withStored, ...geocoded].filter((c) => c.location);
      setCustomers(allCustomers);
      setCustomersLoaded(true);
      logger.log(`Successfully placed ${allCustomers.length} customers on map`);
    } catch (err) {
      logger.log('Error fetching or geocoding customer data:', err);
    } finally {
      setCustomersLoading(false);
    }
  }, [geocodeAddress]);

  // Weekly navigation handlers
  const handlePreviousWeek = useCallback(() => {
    setSelectedWeekStart(prev => navigateWeek(prev, -1));
  }, []);

  const handleNextWeek = useCallback(() => {
    setSelectedWeekStart(prev => navigateWeek(prev, 1));
  }, []);

  const handleCurrentWeek = useCallback(() => {
    setSelectedWeekStart(getWeekStart());
  }, []);

  // Filter maids based on selected filters
  const filteredMaids = maids.filter(maid => {
    // Status filter
    if (!maidStatusFilter.includes(maid.onboardingStatus || 'incomplete')) {
      return false;
    }

    // Supplies filter - only show maids who explicitly have supplies (true)
    if (suppliesFilter && maid.hasSupplies !== true) {
      return false;
    }

    // Available days filter
    if (availableDaysFilter.length > 0) {
      const weeklyAvailability = maid.schedulePreferences?.weeklyAvailability;
      if (!weeklyAvailability) {
        return false; // If no availability data, exclude from results when filter is active
      }

      // Check if maid is available on at least one of the selected days
      const hasMatchingDay = availableDaysFilter.some(day => weeklyAvailability[day] === true);
      if (!hasMatchingDay) {
        return false;
      }
    }

    // Last active filter - show maids active within the last X days
    // Falls back to createdAt for new maids who haven't logged in yet
    if (lastActiveDays !== null) {
      const activeDate = maid.lastLogin || maid.createdAt;
      if (!activeDate) {
        return false;
      }

      const daysSinceActive = (Date.now() - new Date(activeDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceActive > lastActiveDays) {
        return false;
      }
    }

    return true;
  });
  
  const filteredJobs = jobs.filter(job =>
    jobStatusFilter.includes(job.status)
  );

  const filteredCustomers = customers.filter(customer =>
    customerStatusFilter.includes(customer.isActive ? 'active' : 'inactive')
  );

  const handleCustomerStatusFilterChange = useCallback((event) => {
    const value = event.target.value;
    setCustomerStatusFilter(typeof value === 'string' ? value.split(',') : value);
  }, []);

  // Handle compute distance for job - calculates distances from job location to all maids
  const handleComputeDistanceForJob = useCallback(async (job) => {
    if (!job || !job.location) {
      logger.log('Invalid job or job location for distance calculation');
      return;
    }

    // Create a location object from the job
    const jobLocation = {
      lat: job.location.lat,
      lng: job.location.lng,
      address: job.extractedAddress || job.serviceAddress || 'Job Location'
    };

    // Set this as the searched location for distance calculations
    setSearchedLocation(jobLocation);
    
    // Set the job for assignment functionality
    setSelectedJobForAssignment(job);
    
    // Center map on job location
    if (mapRef.current) {
      mapRef.current.panTo(jobLocation);
      mapRef.current.setZoom(14);
    }

    // Calculate distances to all maids using existing function
    await calculateDistancesToMaids(jobLocation, filteredMaids);

    // Show distance panel when calculation is complete
    setShowDistancePanel(true);

    logger.log(`Distance calculation completed for job ${job.id} to all maids`);
  }, [calculateDistancesToMaids, filteredMaids]);

  // Show the nearest technicians to a customer — reuses the job distance flow.
  const handleShowNearbyTechnicians = useCallback(async (customer) => {
    if (!customer || !customer.location) {
      logger.log('Invalid customer or location for nearby-technician lookup');
      return;
    }
    const customerLocation = {
      lat: customer.location.lat,
      lng: customer.location.lng,
      address: customer.extractedAddress || 'Customer Location'
    };
    setSearchedLocation(customerLocation);
    if (mapRef.current) {
      mapRef.current.panTo(customerLocation);
      mapRef.current.setZoom(14);
    }
    await calculateDistancesToMaids(customerLocation, filteredMaids);
    setShowDistancePanel(true);
    logger.log(`Nearby-technician distances computed for customer ${customer.id}`);
  }, [calculateDistancesToMaids, filteredMaids]);

  // Handle double-click on maid marker for assignment
  const handleMaidDoubleClick = useCallback((maid) => {
    // Only allow assignment if there's a selected job and distance calculation is complete
    if (!selectedJobForAssignment || !searchedLocation || calculatingDistances) {
      return;
    }

    // Set the selected maid and open confirmation dialog
    setSelectedMaidForAssignment(maid);
    setAssignmentPayout('');
    setAssignmentDialogOpen(true);
  }, [selectedJobForAssignment, searchedLocation, calculatingDistances]);

  // Handle maid assignment to job
  const handleAssignMaidToJob = useCallback(async () => {
    if (!selectedJobForAssignment || !selectedMaidForAssignment) return;

    setAssigning(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      const token = localStorage.getItem('token');
      
      // Get the job ID (remove job_ prefix if present)
      const jobId = selectedJobForAssignment.id.startsWith('job_') 
        ? selectedJobForAssignment.id.substring(4)  
        : selectedJobForAssignment.id;

      // Get current assignments and add the new maid
      // Use the same ID logic as ServiceSchedulePage to get User ID (not maid profile ID)
      const currentMaidIds = selectedJobForAssignment.maidAssignments?.allMaids?.map(maid => 
        maid.userId || maid.id || maid.user?.id
      ).filter(Boolean) || [];
      
      const newMaidUserId = selectedMaidForAssignment.userId || 
                           selectedMaidForAssignment.id || 
                           selectedMaidForAssignment.user?.id;
      
      if (!newMaidUserId) {
        throw new Error('No valid user ID found for technician');
      }
      
      const updatedMaidIds = [...currentMaidIds, newMaidUserId];

      const response = await axios.post(
        `${apiBase}/api/jobs/${jobId}/assign-maids`,
        // Technicians already on the job keep their payout; the new one needs one
        { maidIds: updatedMaidIds, payouts: { [newMaidUserId]: Number(assignmentPayout) } },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      logger.log('Successfully assigned maid to job:', response.data);
      
      // Update the job in local state
      const newMaidAssignments = {
        ...selectedJobForAssignment.maidAssignments,
        allMaids: [...(selectedJobForAssignment.maidAssignments?.allMaids || []), {
          id: newMaidUserId, // Use the user ID that was actually sent to backend
          userId: newMaidUserId, // Also set userId for consistency
          firstName: selectedMaidForAssignment.firstName,
          lastName: selectedMaidForAssignment.lastName
        }]
      };

      const updatedJob = {
        ...selectedJobForAssignment,
        maidAssignments: newMaidAssignments,
        assignedMaids: newMaidAssignments.allMaids.map(maid => 
          `${maid.firstName} ${maid.lastName}`
        ),
        hasAssignments: true,
        statusColor: jobService.getJobStatusColor(selectedJobForAssignment.status)
      };

      // Update jobs array
      setJobs(prevJobs => 
        prevJobs.map(job => job.id === selectedJobForAssignment.id ? updatedJob : job)
      );

      // Update the selected job for assignment
      setSelectedJobForAssignment(updatedJob);

      // Close dialog and show success
      setAssignmentDialogOpen(false);
      setSelectedMaidForAssignment(null);
      
      // Show success feedback
      alert(`Successfully assigned ${selectedMaidForAssignment.firstName} ${selectedMaidForAssignment.lastName} to job!`);
      
    } catch (error) {
      logger.error('Error assigning maid to job:', error);
      const errorMessage = error.response?.data?.error || 'Failed to assign technician to job.';
      alert(errorMessage);
    } finally {
      setAssigning(false);
    }
  }, [selectedJobForAssignment, selectedMaidForAssignment, assignmentPayout]);

  // Handle creating fake customer from maid profile
  const handleCreateFakeCustomer = useCallback(async (maidProfile) => {
    if (!maidProfile) return;
    
    setCreatingCustomer(true);
    setCustomerCreationResult(null);
    
    try {
      const result = await customerService.createFakeCustomerFromMaid(maidProfile);
      
      setCustomerCreationResult({
        success: true,
        message: 'Test customer created successfully in HousecallPro!',
        customer: result.customer
      });

      logger.log('Fake customer created successfully from maid profile:', result);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setCustomerCreationResult(null);
      }, 5000);
      
    } catch (error) {
      logger.error('Error creating fake customer:', error);
      
      setCustomerCreationResult({
        success: false,
        message: error.message || 'Failed to create test customer. Please try again.',
        error: error
      });
      
      // Clear error message after 8 seconds
      setTimeout(() => {
        setCustomerCreationResult(null);
      }, 8000);
    } finally {
      setCreatingCustomer(false);
    }
  }, []);

  // Format job address using the same logic as ServiceSchedulePage
  const formatJobAddress = (address) => {
    if (!address) return 'Address not available';
    
    // If address is already a formatted string, return it
    if (typeof address === 'string') {
      return address;
    }
    
    // Handle different address field formats (HCP vs local)
    const parts = [
      address.street || address.street_address || address.address_line_1 || address.streetAddress,
      address.street_line_2 || address.address_line_2 || address.streetLine2,
      address.city,
      address.state || address.state_province || address.stateProvince,
      address.zip || address.zip_code || address.postal_code || address.zipCode || address.postalCode
    ].filter(Boolean);
    
    // Also try to extract from nested structures if it exists
    if (parts.length === 0 && address.address) {
      const nestedAddress = address.address;
      const nestedParts = [
        nestedAddress.street || nestedAddress.street_address || nestedAddress.address_line_1,
        nestedAddress.street_line_2 || nestedAddress.address_line_2,
        nestedAddress.city,
        nestedAddress.state || nestedAddress.state_province,
        nestedAddress.zip || nestedAddress.zip_code || nestedAddress.postal_code
      ].filter(Boolean);
      
      if (nestedParts.length > 0) {
        return nestedParts.join(', ');
      }
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Address format not recognized';
  };

  // Calculate total value of filtered jobs
  const totalJobValue = React.useMemo(() => {
    return filteredJobs.reduce((sum, job) => sum + (job.totalAmount || 0), 0);
  }, [filteredJobs]);

  // Get maids with distance data, filtered by max distance
  const maidsWithDistances = filteredMaids.filter(maid => {
    const distance = maidDistances[maid.id];
    if (!distance || !searchedLocation) return true; // Show all if no search
    
    // Convert distance to miles for filtering
    const distanceInMiles = distance.distanceValue ? (distance.distanceValue * 0.000621371) : 0;
    return distanceInMiles <= maxDistance;
  }).map(maid => ({
    ...maid,
    distanceData: maidDistances[maid.id]
  }));

  // Sort maids based on selected criteria
  const sortedMaidsByDistance = [...maidsWithDistances].sort((a, b) => {
    if (!searchedLocation || !a.distanceData || !b.distanceData) {
      // Default sorting when no search location
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'rate':
          return (a.hourlyRate || 0) - (b.hourlyRate || 0);
        case 'experience':
          return (b.experienceLevel || 0) - (a.experienceLevel || 0);
        default:
          return a.firstName.localeCompare(b.firstName);
      }
    }

    switch (sortBy) {
      case 'distance':
        return (a.distanceData?.distanceValue || Infinity) - (b.distanceData?.distanceValue || Infinity);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'rate':
        return (a.hourlyRate || 0) - (b.hourlyRate || 0);
      case 'experience':
        return (b.experienceLevel || 0) - (a.experienceLevel || 0);
      default:
        return (a.distanceData?.distanceValue || Infinity) - (b.distanceData?.distanceValue || Infinity);
    }
  });

  // Calculate distance statistics
  const distanceStats = React.useMemo(() => {
    if (!searchedLocation || Object.keys(maidDistances).length === 0) {
      return null;
    }

    const distances = Object.values(maidDistances)
      .filter(d => d && d.distanceValue && typeof d.distanceValue === 'number')
      .map(d => d.distanceValue * 0.000621371); // Convert meters to miles
    
    if (distances.length === 0) {
      return null;
    }
    
    const within5Miles = distances.filter(d => d <= 5).length;
    const within10Miles = distances.filter(d => d <= 10).length;
    const avgDistance = distances.reduce((sum, d) => sum + d, 0) / distances.length;
    const closest = Math.min(...distances);
    
    return {
      closest: closest.toFixed(1),
      within5Miles,
      within10Miles,
      avgDistance: avgDistance.toFixed(1),
      total: distances.length
    };
  }, [searchedLocation, maidDistances]);

  // Address search handlers
  const handleAddressChange = useCallback((event) => {
    setSearchAddress(event.target.value);
    if (searchError) {
      setSearchError(null); // Clear error when user starts typing
    }
  }, [searchError]);

  const handleSearch = useCallback(async () => {
    if (!searchAddress.trim()) {
      setSearchError('Please enter an address to search');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      const location = await geocodeAddress(searchAddress.trim());
      if (location) {
        setSearchedLocation({
          ...location,
          address: searchAddress.trim(),
          type: 'search'
        });
        logger.log(`Successfully geocoded search address: ${searchAddress} to`, location);
        
        // Optional: Center map on searched location
        if (mapRef.current) {
          mapRef.current.panTo(location);
          mapRef.current.setZoom(14);
        }

        // Calculate distances to all maids
        await calculateDistancesToMaids(location, filteredMaids);
        
        // Show distance panel when search is successful
        setShowDistancePanel(true);
      } else {
        setSearchError('Address not found. Please try a different address.');
      }
    } catch (error) {
      logger.log('Error geocoding search address:', error);
      setSearchError('Unable to locate address. Please check and try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchAddress, geocodeAddress, calculateDistancesToMaids, filteredMaids]);

  const handleClearSearch = useCallback(() => {
    setSearchAddress('');
    setSearchedLocation(null);
    setSearchError(null);
    setMaidDistances({});
    logger.log('Cleared address search');
  }, []);

  // Load maids button handler
  const handleLoadMaids = useCallback(async () => {
    logger.log('Load Maids button clicked', { searchAddress });

    // If address is entered, fetch maids near that address from backend
    if (searchAddress.trim()) {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

        const response = await axios.get(`${apiBase}/api/maids/nearby-address`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            address: searchAddress,
            radiusMiles: maxDistance,
            activeOnly: 'true',
            onboardingStatus: 'approved'
          }
        });

        if (response.data.success) {
          const { data: nearbyMaids, searchCenter } = response.data;

          logger.log(`Loaded ${nearbyMaids.length} maids near address from backend`, {
            totalFound: nearbyMaids.length,
            searchAddress,
            searchCenter
          });

          // Transform backend response to match frontend maid structure
          const transformedMaids = nearbyMaids.map(maid => ({
            ...maid,
            location: { lat: maid.latitude, lng: maid.longitude }
          }));

          setMaids(transformedMaids);
          setSearchedLocation(searchCenter);
          setMaidsLoaded(true);
          setMaidsFilterMode('address'); // Mark as address-filtered

          // Calculate distances for display
          const distances = {};
          nearbyMaids.forEach(maid => {
            distances[maid.id] = {
              distance: maid.distanceDisplay,  // Formatted string like "5.2 mi"
              distanceValue: maid.distance * 1609.34,  // Convert miles to meters for consistency
              duration: estimateDrivingTime(maid.distance),  // Estimate drive time
              durationValue: null  // No precise duration from backend
            };
          });
          setMaidDistances(distances);

          // Center map on searched location
          if (searchCenter) {
            setMapCenter(searchCenter);
          }
        } else {
          setError('Failed to load technicians near the address');
        }
      } catch (err) {
        logger.log('Error loading maids near address:', err);
        setError(err.response?.data?.error || 'Failed to load technicians. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // No address entered, load all maids normally
      setMaidsLoaded(true);
      setMaidsFilterMode('all'); // Mark as loading all maids
      await fetchMaidsAndLocations();
    }
  }, [searchAddress, maxDistance, fetchMaidsAndLocations]);

  // Get closest maid to searched location
  const getClosestMaid = useCallback(() => {
    if (!searchedLocation || Object.keys(maidDistances).length === 0) {
      return null;
    }

    let closestMaid = null;
    let shortestDistance = Infinity;

    filteredMaids.forEach(maid => {
      const distance = maidDistances[maid.id];
      if (distance?.distanceValue && distance.distanceValue < shortestDistance) {
        shortestDistance = distance.distanceValue;
        closestMaid = { ...maid, ...distance };
      }
    });

    return closestMaid;
  }, [searchedLocation, maidDistances, filteredMaids]);

  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Filter handlers
  const handleMaidStatusFilterChange = useCallback((event) => {
    const value = event.target.value;
    setMaidStatusFilter(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleJobStatusFilterChange = useCallback((event) => {
    const value = event.target.value;
    setJobStatusFilter(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleSuppliesFilterChange = useCallback((event) => {
    setSuppliesFilter(event.target.checked);
  }, []);

  const handleAvailableDaysFilterChange = useCallback((event) => {
    const value = event.target.value;
    setAvailableDaysFilter(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleLastActiveDaysChange = useCallback((event) => {
    const value = event.target.value;
    setLastActiveDays(value === '' ? null : parseInt(value, 10));
  }, []);

  // Combined function to fetch both maids and jobs
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        // Only auto-fetch maids if they're loaded AND not in address-filtered mode
        showMaids && maidsLoaded && maidsFilterMode === 'all' ? fetchMaidsAndLocations() : Promise.resolve(),
        showJobs ? fetchJobsAndLocations(selectedWeekStart) : Promise.resolve()
      ]);

      // Log overall cache performance summary
      const cacheStats = googleMapsCache.getCacheStats();
      logger.log(`📊 Google Maps Cache Statistics:`, cacheStats);

      setLoading(false);
    } catch (err) {
      logger.log('Error fetching data:', err);
      setError(err.message || 'Failed to load map data. Please try again.');
      setLoading(false);
    }
  }, [fetchMaidsAndLocations, fetchJobsAndLocations, showMaids, showJobs, selectedWeekStart, maidsLoaded, maidsFilterMode]);

  // Set initial map center based on REACT_APP_CITY_CODE
  useEffect(() => {
    const appCityCode = process.env.REACT_APP_CITY_CODE || 'MCO'; // Default to MCO
    const center = cityCenters[appCityCode] || cityCenters['MCO']; // Fallback to MCO if not found
    setMapCenter(center);
  }, []);

  // Fetch data when week changes
  useEffect(() => {
    if (isLoaded && mapCenter && showJobs) {
      fetchJobsAndLocations(selectedWeekStart);
    }
  }, [isLoaded, mapCenter, selectedWeekStart, showJobs, fetchJobsAndLocations]);

  // Lazily load customers the first time the layer is turned on (avoids
  // geocoding the whole customer base on mount).
  useEffect(() => {
    if (isLoaded && mapCenter && showCustomers && !customersLoaded && !customersLoading) {
      fetchCustomersAndLocations();
    }
  }, [isLoaded, mapCenter, showCustomers, customersLoaded, customersLoading, fetchCustomersAndLocations]);

  // Fetch data initially and set up refresh interval
  useEffect(() => {
    if (isLoaded && mapCenter) {
      fetchAllData(); // Initial fetch

      const intervalId = setInterval(() => {
        fetchAllData(); // Refresh every 10 minutes
      }, 600000); // 10 minutes

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [isLoaded, mapCenter, fetchAllData]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (loadError) {
    return (
      <Box sx={{
        position: 'fixed',
        top: APPBAR_HEIGHT,
        left: 0,
        width: '100vw',
        height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        bgcolor: 'background.paper'
      }}>
        <Alert severity="error">
          Error loading Google Maps: {loadError.message}. Please check your API key.
        </Alert>
      </Box>
    );
  }

  if (!isLoaded || !mapCenter) {
    return (
      <Box sx={{
        position: 'fixed',
        top: APPBAR_HEIGHT,
        left: 0,
        width: '100vw',
        height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        bgcolor: 'background.paper'
      }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading map...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <AdminPageLayout fullWidth title="Technician Map">
    <Box sx={{ display: 'flex', width: '100%', height: `calc(100vh - ${APPBAR_HEIGHT}px)` }}>
      {/* Left Sidebar - Controls */}
      <Box sx={{
        width: { xs: '100vw', sm: 400 },
        height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
        bgcolor: 'background.paper',
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: { xs: 'absolute', sm: 'relative' },
        zIndex: { xs: 20, sm: 'auto' },
        transform: { xs: showMobileSidebar ? 'translateX(0)' : 'translateX(-100%)', sm: 'none' },
        transition: { xs: 'transform 0.3s ease-in-out', sm: 'none' }
      }}>
        {/* Header Section */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" fontWeight={600}>
              Service Map Dashboard
            </Typography>
            {/* Mobile Close Button */}
            <IconButton 
              sx={{ display: { xs: 'block', sm: 'none' } }}
              onClick={() => setShowMobileSidebar(false)}
            >
              <ClearIcon />
            </IconButton>
          </Box>
          
          {/* Job Total Value */}
          {showJobs && filteredJobs.length > 0 && (
            <Box sx={{
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              border: '2px solid #f44336',
              borderRadius: 1,
              px: 2,
              py: 1,
              mb: 2,
              textAlign: 'center'
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#f44336', 
                  fontWeight: 'bold'
                }}
              >
                Total Week Value: ${(totalJobValue / 100).toFixed(2)}
              </Typography>
            </Box>
          )}

          {/* Loading/Error Messages */}
          {(loading || jobsLoading) && (
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {loading ? 'Loading map data...' : 'Loading jobs for selected week...'}
              </Typography>
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Weekly Navigation Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              📅 Week Navigation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton 
                onClick={handlePreviousWeek}
                size="small"
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <ChevronLeft />
              </IconButton>
              
              <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                {formatWeekDisplay(selectedWeekStart)}
              </Typography>
              
              <IconButton 
                onClick={handleNextWeek}
                size="small"
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
            <Button
              onClick={handleCurrentWeek}
              size="small"
              startIcon={<Today />}
              variant="outlined"
              fullWidth
            >
              Jump to Current Week
            </Button>
          </Box>

          {/* Load Maids Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              🧹 Technician Data
            </Typography>
            {!maidsLoaded ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.9rem' }}>
                Click "Load Technicians" to display technician locations on the map.
                {searchAddress.trim() && ' Technicians will be filtered by the address entered below.'}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2, fontSize: '0.9rem' }}>
                {maids.length} technician{maids.length !== 1 ? 's' : ''} loaded
                {searchedLocation && ` within ${maxDistance} miles`}
                {maidsFilterMode === 'address' && ' (auto-refresh disabled for filtered results)'}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleLoadMaids}
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? 'Loading...' : maidsLoaded ? 'Reload Technicians' : 'Load Technicians'}
              </Button>
              {maidsLoaded && (
                <Button
                  onClick={() => {
                    setMaidsLoaded(false);
                    setMaidsFilterMode(null);
                    setMaids([]);
                    setMaidDistances({});
                    setSearchedLocation(null);
                  }}
                  variant="outlined"
                  color="secondary"
                  disabled={loading}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          {/* Address Search Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              📍 Address Search
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Search address..."
                value={searchAddress}
                onChange={handleAddressChange}
                onKeyPress={handleKeyPress}
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchLoading ? <CircularProgress size={16} /> : null
                }}
              />
              <Button 
                variant="outlined" 
                onClick={handleSearch}
                disabled={searchLoading || calculatingDistances || !searchAddress.trim()}
                size="small"
                startIcon={calculatingDistances ? <CircularProgress size={14} /> : null}
                sx={{ minWidth: 60 }}
              >
                {calculatingDistances ? 'Calc' : 'Go'}
              </Button>
            </Box>
            
            {searchError && (
              <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem', py: 0.5 }}>
                {searchError}
              </Alert>
            )}

            {searchedLocation && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Max Distance"
                  type="number"
                  value={maxDistance}
                  onChange={(e) => setMaxDistance(Math.max(1, parseInt(e.target.value) || 25))}
                  sx={{ width: 120 }}
                  InputProps={{
                    endAdornment: <Typography variant="caption" sx={{ mr: 1 }}>mi</Typography>
                  }}
                />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="Sort By"
                  >
                    <MenuItem value="distance">Distance</MenuItem>
                    <MenuItem value="rating">Rating</MenuItem>
                    <MenuItem value="rate">Hourly Rate</MenuItem>
                    <MenuItem value="experience">Experience</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {searchedLocation && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Toggle distance visualization">
                  <IconButton 
                    onClick={() => setShowDistanceRadius(!showDistanceRadius)}
                    size="small"
                    sx={{ 
                      bgcolor: showDistanceRadius ? 'primary.main' : 'grey.300', 
                      color: showDistanceRadius ? 'white' : 'grey.600',
                      '&:hover': { 
                        bgcolor: showDistanceRadius ? 'primary.dark' : 'grey.400' 
                      } 
                    }}
                  >
                    {showDistanceRadius ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </Tooltip>
                <Button 
                  onClick={handleClearSearch}
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  sx={{ flex: 1 }}
                >
                  Clear Search
                </Button>
                <Tooltip title="Clear Google Maps Cache">
                  <IconButton 
                    onClick={() => {
                      googleMapsCache.clearCache();
                      logger.log('Google Maps cache cleared by user');
                      alert('Google Maps cache cleared successfully!');
                    }}
                    size="small"
                    sx={{ bgcolor: 'warning.main', color: 'white', '&:hover': { bgcolor: 'warning.dark' } }}
                  >
                    💾
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Distance Summary Cards */}
          {searchedLocation && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                📊 Distance Analysis
              </Typography>
              {calculatingDistances ? (
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Calculating distances...</Typography>
                  </Box>
                </Card>
              ) : distanceStats ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Card sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Closest</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {distanceStats.closest} mi
                    </Typography>
                  </Card>
                  
                  <Card sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Within 5mi</Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {distanceStats.within5Miles}
                    </Typography>
                  </Card>
                  
                  <Card sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Within 10mi</Typography>
                    <Typography variant="h6" color="secondary.main" fontWeight="bold">
                      {distanceStats.within10Miles}
                    </Typography>
                  </Card>
                  
                  <Card sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Avg Distance</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {distanceStats.avgDistance} mi
                    </Typography>
                  </Card>
                </Box>
              ) : Object.keys(maidDistances).length === 0 ? (
                <Card sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No distance data available
                  </Typography>
                </Card>
              ) : null}
            </Box>
          )}

          {/* View Toggles Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              👁️ View Options
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showMaids}
                    onChange={(e) => setShowMaids(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={`Show Technicians (${searchedLocation ? maidsWithDistances.length : filteredMaids.length}/${maids.length})`}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showJobs}
                    onChange={(e) => setShowJobs(e.target.checked)}
                    color="secondary"
                    size="small"
                  />
                }
                label={`Show Jobs (${filteredJobs.length}/${jobs.length})`}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showCustomers}
                    onChange={(e) => setShowCustomers(e.target.checked)}
                    color="success"
                    size="small"
                  />
                }
                label={
                  customersLoading
                    ? 'Show Customers (loading…)'
                    : `Show Customers (${filteredCustomers.length}/${customers.length})`
                }
              />
            </Box>
          </Box>
          
          {/* Filter Controls Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              🔍 Filters
            </Typography>
            {showMaids && (
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Technician Status Filter</InputLabel>
                <Select
                  multiple
                  value={maidStatusFilter}
                  onChange={handleMaidStatusFilterChange}
                  renderValue={(selected) => `${selected.length} status selected`}
                  label="Technician Status Filter"
                >
                  <MenuItem value="approved">
                    <Checkbox checked={maidStatusFilter.indexOf('approved') > -1} />
                    <ListItemText primary="Approved" />
                  </MenuItem>
                  <MenuItem value="pending_review">
                    <Checkbox checked={maidStatusFilter.indexOf('pending_review') > -1} />
                    <ListItemText primary="Pending Review" />
                  </MenuItem>
                  <MenuItem value="incomplete">
                    <Checkbox checked={maidStatusFilter.indexOf('incomplete') > -1} />
                    <ListItemText primary="Incomplete" />
                  </MenuItem>
                  <MenuItem value="rejected">
                    <Checkbox checked={maidStatusFilter.indexOf('rejected') > -1} />
                    <ListItemText primary="Rejected" />
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            
            {showJobs && (
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Job Status Filter</InputLabel>
                <Select
                  multiple
                  value={jobStatusFilter}
                  onChange={handleJobStatusFilterChange}
                  renderValue={(selected) => `${selected.length} status selected`}
                  label="Job Status Filter"
                >
                  <MenuItem value="NOT_STARTED">
                    <Checkbox checked={jobStatusFilter.indexOf('NOT_STARTED') > -1} />
                    <ListItemText primary="Not Started" />
                  </MenuItem>
                  <MenuItem value="IN_PROGRESS">
                    <Checkbox checked={jobStatusFilter.indexOf('IN_PROGRESS') > -1} />
                    <ListItemText primary="In Progress" />
                  </MenuItem>
                  <MenuItem value="COMPLETED">
                    <Checkbox checked={jobStatusFilter.indexOf('COMPLETED') > -1} />
                    <ListItemText primary="Completed" />
                  </MenuItem>
                  <MenuItem value="CANCELLED">
                    <Checkbox checked={jobStatusFilter.indexOf('CANCELLED') > -1} />
                    <ListItemText primary="Cancelled" />
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {showCustomers && (
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Customer Status Filter</InputLabel>
                <Select
                  multiple
                  value={customerStatusFilter}
                  onChange={handleCustomerStatusFilterChange}
                  renderValue={(selected) => `${selected.length} status selected`}
                  label="Customer Status Filter"
                >
                  <MenuItem value="active">
                    <Checkbox checked={customerStatusFilter.indexOf('active') > -1} />
                    <ListItemText primary="Active" />
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Checkbox checked={customerStatusFilter.indexOf('inactive') > -1} />
                    <ListItemText primary="Inactive" />
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {showMaids && (
              <>
                {/* Supplies Filter */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={suppliesFilter}
                      onChange={handleSuppliesFilterChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Technician has supplies"
                  sx={{ mb: 1, width: '100%' }}
                />

                {/* Available Days Filter */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Available Days</InputLabel>
                  <Select
                    multiple
                    value={availableDaysFilter}
                    onChange={handleAvailableDaysFilterChange}
                    renderValue={(selected) => 
                      selected.length === 0 
                        ? 'Any day' 
                        : selected.length === 7 
                          ? 'All days' 
                          : `${selected.length} day${selected.length > 1 ? 's' : ''} selected`
                    }
                    label="Available Days"
                  >
                    <MenuItem value="monday">
                      <Checkbox checked={availableDaysFilter.indexOf('monday') > -1} />
                      <ListItemText primary="Monday" />
                    </MenuItem>
                    <MenuItem value="tuesday">
                      <Checkbox checked={availableDaysFilter.indexOf('tuesday') > -1} />
                      <ListItemText primary="Tuesday" />
                    </MenuItem>
                    <MenuItem value="wednesday">
                      <Checkbox checked={availableDaysFilter.indexOf('wednesday') > -1} />
                      <ListItemText primary="Wednesday" />
                    </MenuItem>
                    <MenuItem value="thursday">
                      <Checkbox checked={availableDaysFilter.indexOf('thursday') > -1} />
                      <ListItemText primary="Thursday" />
                    </MenuItem>
                    <MenuItem value="friday">
                      <Checkbox checked={availableDaysFilter.indexOf('friday') > -1} />
                      <ListItemText primary="Friday" />
                    </MenuItem>
                    <MenuItem value="saturday">
                      <Checkbox checked={availableDaysFilter.indexOf('saturday') > -1} />
                      <ListItemText primary="Saturday" />
                    </MenuItem>
                    <MenuItem value="sunday">
                      <Checkbox checked={availableDaysFilter.indexOf('sunday') > -1} />
                      <ListItemText primary="Sunday" />
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Last Active Filter */}
                <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Last Active</InputLabel>
                  <Select
                    value={lastActiveDays === null ? '' : lastActiveDays}
                    onChange={handleLastActiveDaysChange}
                    label="Last Active"
                  >
                    <MenuItem value="">Any time</MenuItem>
                    <MenuItem value={1}>Last 24 hours</MenuItem>
                    <MenuItem value={3}>Last 3 days</MenuItem>
                    <MenuItem value={7}>Last week</MenuItem>
                    <MenuItem value={14}>Last 2 weeks</MenuItem>
                    <MenuItem value={30}>Last month</MenuItem>
                    <MenuItem value={60}>Last 2 months</MenuItem>
                    <MenuItem value={90}>Last 3 months</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>

          {/* Legend Section - Compact */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              🗺️ Legend
            </Typography>
            
            {/* Maid Legend */}
            {showMaids && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom>Technicians:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label="📍 Approved" sx={{ bgcolor: '#F44336', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="📍 Pending" sx={{ bgcolor: '#FF9800', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="📍 Incomplete" sx={{ bgcolor: '#9E9E9E', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="📍 Rejected" sx={{ bgcolor: '#424242', color: 'white', fontSize: '0.7rem' }} />
                </Box>
              </Box>
            )}
            
            {/* Job Legend */}
            {showJobs && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom>Jobs:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" label="● Not Started" sx={{ bgcolor: '#FFA500', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="● In Progress" sx={{ bgcolor: '#4CAF50', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="● Completed" sx={{ bgcolor: '#2196F3', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="● Cancelled" sx={{ bgcolor: '#F44336', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="● Unassigned" sx={{ bgcolor: '#F44336', color: 'white', fontSize: '0.7rem' }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                  Circle size = job value: S($0-99) M($100-199) L($200-299) XL($300-499) XXL($500+)<br/>
                  Jobs without assigned technicians appear red regardless of status
                </Typography>
              </Box>
            )}
            
            {/* Customer Legend */}
            {showCustomers && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom>Customers:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label="🏠 Active" sx={{ bgcolor: '#2E7D32', color: 'white', fontSize: '0.7rem' }} />
                  <Chip size="small" label="🏠 Inactive" sx={{ bgcolor: '#607D8B', color: 'white', fontSize: '0.7rem' }} />
                </Box>
              </Box>
            )}

            {/* Search Legend */}
            {searchedLocation && (
              <Box>
                <Typography variant="body2" fontWeight={500} gutterBottom>Search:</Typography>
                <Chip size="small" label="⭐ Search Location" sx={{ bgcolor: '#9C27B0', color: 'white', fontSize: '0.7rem' }} />
              </Box>
            )}
          </Box>

          {/* Debug Info */}
          {searchedLocation && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Debug: SearchLocation: {searchedLocation ? 'Yes' : 'No'} | 
                MaidDistances: {Object.keys(maidDistances).length} | 
                DistanceStats: {distanceStats ? 'Yes' : 'No'} |
                CalcDistances: {calculatingDistances ? 'Yes' : 'No'} |
                Cache: {(() => {
                  const stats = googleMapsCache.getCacheStats();
                  return `G:${stats.geocoding.hits}/${stats.geocoding.hits + stats.geocoding.misses} D:${stats.distance.hits}/${stats.distance.hits + stats.distance.misses}`;
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Side - Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {/* Mobile Controls Toggle Button */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 15,
            bgcolor: 'primary.main',
            color: 'white',
            display: { xs: 'flex', sm: 'none' },
            '&:hover': { bgcolor: 'primary.dark' }
          }}
          onClick={() => setShowMobileSidebar(true)}
        >
          <SearchIcon />
        </IconButton>

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={11}
          options={defaultMapOptions}
          onLoad={onMapLoad}
        >
        {/* Distance Radius Visualization */}
        {searchedLocation && showDistanceRadius && (
          <>
            <Circle
              center={searchedLocation}
              radius={5 * 1609.34} // 5 miles in meters
              options={{
                fillColor: '#4CAF50',
                fillOpacity: 0.1,
                strokeColor: '#4CAF50',
                strokeOpacity: 0.4,
                strokeWeight: 2
              }}
            />
            <Circle
              center={searchedLocation}
              radius={10 * 1609.34} // 10 miles in meters
              options={{
                fillColor: '#FF9800',
                fillOpacity: 0.05,
                strokeColor: '#FF9800',
                strokeOpacity: 0.3,
                strokeWeight: 1
              }}
            />
            <Circle
              center={searchedLocation}
              radius={maxDistance * 1609.34} // Max distance in meters
              options={{
                fillColor: '#F44336',
                fillOpacity: 0.03,
                strokeColor: '#F44336',
                strokeOpacity: 0.5,
                strokeWeight: 2,
                strokeDashArray: [5, 5]
              }}
            />
          </>
        )}

        {/* Maid Markers */}
        {showMaids && (searchedLocation ? maidsWithDistances : filteredMaids).map((maid) => {
          const pinColor = getMaidPinColor(maid.onboardingStatus);
          const strokeColor = pinColor === '#F44336' ? '#D32F2F' : 
                             pinColor === '#FF9800' ? '#F57C00' : 
                             pinColor === '#9E9E9E' ? '#757575' : '#212121';
          
          // Check if this is the closest maid
          const closestMaid = getClosestMaid();
          const isClosest = closestMaid && closestMaid.id === maid.id;
          const distance = maidDistances[maid.id];
          
          return (
            <React.Fragment key={`maid-${maid.id}`}>
              {maid.location && typeof maid.location.lat === 'number' && typeof maid.location.lng === 'number' && (
                <Marker
                  position={maid.location}
                  title={`Technician: ${maid.firstName} ${maid.lastName} (${maid.onboardingStatus || 'unknown'})${
                    isClosest ? ' - CLOSEST' : ''
                  }${distance ? ` - ${distance.distance}, ${distance.duration}` : ''}`}
                  icon={{
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                    fillColor: pinColor,
                    fillOpacity: 0.9,
                    strokeColor: isClosest ? '#FFD700' : strokeColor, // Gold stroke for closest
                    strokeWeight: isClosest ? 4 : 2, // Thicker stroke for closest
                    scale: isClosest ? 2.2 : 1.5, // Larger scale for closest
                    anchor: new window.google.maps.Point(12, 24)
                  }}
                  onClick={() => setSelectedMarker({ ...maid, type: 'maid' })}
                  onDoubleClick={() => handleMaidDoubleClick(maid)}
                />
              )}
              
              {/* Distance Label Overlay */}
              {searchedLocation && distance && maid.location && typeof maid.location.lat === 'number' && typeof maid.location.lng === 'number' && (
                <OverlayView
                  position={{
                    lat: maid.location.lat + 0.003, // Offset above marker
                    lng: maid.location.lng
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Paper sx={{ 
                    p: 0.5, 
                    bgcolor: isClosest ? '#FFD700' : 'white',
                    color: isClosest ? '#000' : 'inherit',
                    border: `1px solid ${isClosest ? '#FFA000' : '#ccc'}`,
                    borderRadius: 1,
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    minWidth: '60px',
                    boxShadow: 2,
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMarker({ ...maid, type: 'maid' });
                  }}
                  >
                    <Typography variant="caption" fontWeight={isClosest ? 'bold' : 'normal'} sx={{ fontSize: '0.65rem' }}>
                      {distance.distance}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {distance.duration}
                    </Typography>
                    {isClosest && (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        🏆 CLOSEST
                      </Typography>
                    )}
                  </Paper>
                </OverlayView>
              )}
            </React.Fragment>
          );
        })}

        {/* Job Markers */}
        {showJobs && (() => {
          // Remove duplicates
          const uniqueJobs = filteredJobs.filter((job, index, self) => 
            index === self.findIndex(j => j.id === job.id)
          );
          return uniqueJobs.map((job) => (
            job.location && typeof job.location.lat === 'number' && typeof job.location.lng === 'number' && (
              <Marker
                key={`job-${job.id}`}
                position={job.location}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: job.circleSize || 8,
                  fillColor: job.statusColor,
                  fillOpacity: 0.9,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                }}
                onClick={() => {
                  setSelectedMarker({ ...job, type: 'job' });
                }}
              />
            )
          ));
        })()}

        {/* Customer Markers (clustered — house icons, colored by status) */}
        {showCustomers && (
          <MarkerClusterer options={{ maxZoom: 14 }}>
            {(clusterer) =>
              filteredCustomers.map((customer) => (
                customer.location &&
                typeof customer.location.lat === 'number' &&
                typeof customer.location.lng === 'number' && (
                  <Marker
                    key={`customer-${customer.id}`}
                    position={customer.location}
                    clusterer={clusterer}
                    title={customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                    icon={{
                      path: 'M12 3l9 8h-3v9h-5v-6h-2v6H6v-9H3z',
                      fillColor: customer.isActive ? '#2E7D32' : '#607D8B',
                      fillOpacity: 0.95,
                      strokeColor: '#FFFFFF',
                      strokeWeight: 1.5,
                      scale: 1.4,
                      anchor: new window.google.maps.Point(12, 12)
                    }}
                    onClick={() => setSelectedMarker({ ...customer, type: 'customer' })}
                  />
                )
              ))
            }
          </MarkerClusterer>
        )}

        {/* Search Address Marker */}
        {searchedLocation && typeof searchedLocation.lat === 'number' && typeof searchedLocation.lng === 'number' && (
          <Marker
            key="search-marker"
            position={searchedLocation}
            title={`Searched Address: ${searchedLocation.address}`}
            icon={{
              path: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
              fillColor: '#9C27B0',
              fillOpacity: 0.9,
              strokeColor: '#7B1FA2',
              strokeWeight: 2,
              scale: 2,
              anchor: new window.google.maps.Point(12, 12)
            }}
            onClick={() => setSelectedMarker({ 
              ...searchedLocation, 
              type: 'search',
              location: searchedLocation
            })}
          />
        )}

        {/* Info Window - Only render one at a time */}
        {selectedMarker && selectedMarker.location && selectedMarker.type && (
          <InfoWindow
            key={`infowindow-${selectedMarker.type}-${selectedMarker.id || 'search'}`}
            position={selectedMarker.location}
            onCloseClick={() => setSelectedMarker(null)}
            options={{
              disableAutoPan: false,
              maxWidth: 320,
              pixelOffset: new window.google.maps.Size(0, -10)
            }}
          >
            <Box sx={{ p: 1, maxWidth: 300 }}>
              {selectedMarker.type === 'maid' ? (
                <div>
                  <Typography variant="h6" gutterBottom>
                    {selectedMarker.firstName} {selectedMarker.lastName}
                    {(() => {
                      const closestMaid = getClosestMaid();
                      return closestMaid && closestMaid.id === selectedMarker.id ? (
                        <Chip 
                          size="small" 
                          label="🏆 CLOSEST" 
                          sx={{ 
                            ml: 1, 
                            bgcolor: '#FFD700', 
                            color: '#000',
                            fontSize: '0.6rem',
                            fontWeight: 'bold'
                          }} 
                        />
                      ) : null;
                    })()}
                  </Typography>

                  {/* Focused Job Information */}
                  {selectedJobForAssignment && (
                    <Paper sx={{ 
                      p: 1.5, 
                      bgcolor: 'success.light', 
                      color: 'success.contrastText',
                      mb: 2,
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" fontWeight="bold" align="center" sx={{ mb: 1 }}>
                        🎯 Focused Job for Assignment
                      </Typography>
                      <Typography variant="body2" align="center">
                        <strong>Job #{selectedJobForAssignment.jobNumber || selectedJobForAssignment.id}</strong>
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ fontSize: '0.8rem' }}>
                        {selectedJobForAssignment.customerName} • ${(selectedJobForAssignment.totalAmount / 100).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ fontSize: '0.8rem' }}>
                        {selectedJobForAssignment.serviceDate}
                      </Typography>
                    </Paper>
                  )}

                  {/* Distance Information - Prominent Display */}
                  {searchedLocation && maidDistances[selectedMarker.id] && (
                    <Paper sx={{ 
                      p: 1.5, 
                      bgcolor: 'primary.light', 
                      color: 'primary.contrastText',
                      mb: 2,
                      borderRadius: 1
                    }}>
                      <Typography variant="body2" fontWeight="bold" align="center" sx={{ mb: 1 }}>
                        🎯 Distance from Search Location
                      </Typography>
                      <Stack direction="row" spacing={2} justifyContent="center">
                        <Box textAlign="center">
                          <Typography variant="h6" fontWeight="bold">
                            {maidDistances[selectedMarker.id].distance}
                          </Typography>
                          <Typography variant="caption">Distance</Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="h6" fontWeight="bold">
                            {maidDistances[selectedMarker.id].duration}
                          </Typography>
                          <Typography variant="caption">Drive Time</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {/* Maid Details */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Status:</strong> 
                    <Chip 
                      size="small" 
                      label={selectedMarker.onboardingStatus || 'unknown'} 
                      sx={{ 
                        ml: 1, 
                        bgcolor: getMaidPinColor(selectedMarker.onboardingStatus), 
                        color: 'white',
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Experience:</strong> {selectedMarker.experienceLevel || 'Not specified'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Rate:</strong> ${selectedMarker.hourlyRate || 'N/A'}/hour
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Rating:</strong> {selectedMarker.rating || 'No rating'} ⭐
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <strong>Phone:</strong> {selectedMarker.phoneNumber || 'Not provided'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Address:</strong>{' '}
                    {selectedMarker.residentialAddress ? (
                      <Typography
                        component="a"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedMarker.residentialAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {selectedMarker.residentialAddress}
                      </Typography>
                    ) : (
                      'Not provided'
                    )}
                  </Typography>

                  {/* Customer creation result message */}
                  {customerCreationResult && (
                    <Alert 
                      severity={customerCreationResult.success ? 'success' : 'error'} 
                      sx={{ mt: 1, fontSize: '0.75rem' }}
                    >
                      {customerCreationResult.message}
                      {customerCreationResult.success && customerCreationResult.customer && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          Customer ID: {customerCreationResult.customer.id}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Assign to Job Button - only show if there's a focused job */}
                    {selectedJobForAssignment && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleMaidDoubleClick(selectedMarker)}
                        disabled={assigning}
                        startIcon={assigning ? <CircularProgress size={14} /> : null}
                        sx={{ width: '100%' }}
                      >
                        {assigning ? 'Assigning...' : `Assign to Job #${selectedJobForAssignment.jobNumber || selectedJobForAssignment.id}`}
                      </Button>
                    )}

                    {/* Sync to Houcall Pro Button */}
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={creatingCustomer ? <CircularProgress size={14} /> : <PersonAdd />}
                      onClick={() => handleCreateFakeCustomer(selectedMarker)}
                      disabled={creatingCustomer}
                      sx={{ width: '100%' }}
                    >
                      {creatingCustomer ? 'Creating...' : 'Sync to Houcall Pro'}
                    </Button>

                    {/* Get Directions Button */}
                    {searchedLocation && maidDistances[selectedMarker.id] && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DirectionsIcon />}
                        onClick={() => {
                          const maidAddress = selectedMarker.residentialAddress;
                          const searchAddress = searchedLocation.address;
                          const directionsUrl = `https://www.google.com/maps/dir/${encodeURIComponent(searchAddress)}/${encodeURIComponent(maidAddress)}`;
                          window.open(directionsUrl, '_blank');
                        }}
                        sx={{ width: '100%' }}
                      >
                        Get Directions
                      </Button>
                    )}
                  </Box>
                </div>
              ) : selectedMarker.type === 'search' ? (
                <div>
                  <Typography variant="h6" gutterBottom>
                    📍 Searched Location
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Address:</strong> {selectedMarker.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Coordinates:</strong> {selectedMarker.lat.toFixed(6)}, {selectedMarker.lng.toFixed(6)}
                  </Typography>
                  
                  {calculatingDistances && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Calculating distances...
                      </Typography>
                    </Box>
                  )}
                  
                  {!calculatingDistances && (() => {
                    const closestMaid = getClosestMaid();
                    return closestMaid ? (
                      <>
                        <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                          🎯 Closest Technician:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>{closestMaid.firstName} {closestMaid.lastName}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          📍 {closestMaid.distance} • 🚗 {closestMaid.duration}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: <Chip 
                            size="small" 
                            label={closestMaid.onboardingStatus || 'unknown'} 
                            sx={{ 
                              ml: 0.5, 
                              bgcolor: getMaidPinColor(closestMaid.onboardingStatus), 
                              color: 'white',
                              fontSize: '0.6rem',
                              height: '18px'
                            }} 
                          />
                        </Typography>
                      </>
                    ) : Object.keys(maidDistances).length > 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No technicians available for distance calculation
                      </Typography>
                    ) : null;
                  })()}
                </div>
              ) : selectedMarker.type === 'job' ? (
                <div>
                  {/* Only render job details if we have valid job data */}
                  {selectedMarker.id ? (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Job #{selectedMarker.jobNumber || selectedMarker.orderId || selectedMarker.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Customer:</strong> {selectedMarker.customerName || 'Unknown Customer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Email:</strong> {selectedMarker.customerEmail || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Phone:</strong> {selectedMarker.customerPhone || 'N/A'}
                      </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> 
                    <Chip 
                      size="small" 
                      label={selectedMarker.status.replace('_', ' ')} 
                      sx={{ 
                        ml: 1, 
                        bgcolor: selectedMarker.statusColor, 
                        color: 'white',
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Service Date:</strong> {selectedMarker.serviceDate || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Service:</strong> {selectedMarker.serviceDescription || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Address:</strong> {selectedMarker.extractedAddress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Amount:</strong> ${(selectedMarker.totalAmount / 100).toFixed(2)}
                      </Typography>
                  {selectedMarker.assignedMaids && selectedMarker.assignedMaids.length > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Assigned:</strong> {selectedMarker.assignedMaids.join(', ')}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Assigned:</strong> <em>No technicians assigned yet</em>
                    </Typography>
                  )}
                  {selectedMarker.specialInstructions && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Instructions:</strong> {selectedMarker.specialInstructions}
                    </Typography>
                  )}
                  
                  {/* Compute Distance Button for Jobs */}
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleComputeDistanceForJob(selectedMarker)}
                      disabled={calculatingDistances}
                      startIcon={calculatingDistances ? <CircularProgress size={14} /> : null}
                      sx={{ 
                        fontSize: '0.75rem',
                        textTransform: 'none'
                      }}
                    >
                      {calculatingDistances ? 'Computing...' : 'Compute Distance to All Technicians'}
                    </Button>
                    
                    {/* Show closest maid info if distances are calculated */}
                    {!calculatingDistances && Object.keys(maidDistances).length > 0 && (() => {
                      const closestMaid = getClosestMaid();
                      return closestMaid ? (
                        <Box sx={{ 
                          bgcolor: 'primary.light', 
                          color: 'primary.contrastText', 
                          p: 1, 
                          borderRadius: 1,
                          mt: 1
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            🎯 Closest Technician: {closestMaid.firstName} {closestMaid.lastName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            📍 {closestMaid.distance} • 🚗 {closestMaid.duration}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 1 }}>
                          No technicians available for distance calculation
                        </Typography>
                      );
                    })()}
                  </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Loading job details...
                    </Typography>
                  )}
                </div>
              ) : selectedMarker.type === 'customer' ? (
                <div>
                  <Typography variant="h6" gutterBottom>
                    {selectedMarker.fullName ||
                      `${selectedMarker.firstName || ''} ${selectedMarker.lastName || ''}`.trim() ||
                      'Customer'}
                    <Chip
                      size="small"
                      label={selectedMarker.isActive ? 'Active' : 'Inactive'}
                      sx={{
                        ml: 1,
                        bgcolor: selectedMarker.isActive ? '#2E7D32' : '#607D8B',
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {selectedMarker.email || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Phone:</strong> {selectedMarker.phoneNumber || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Address:</strong> {selectedMarker.extractedAddress || 'N/A'}
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DirectionsIcon />}
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedMarker.extractedAddress || '')}`,
                          '_blank'
                        )
                      }
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Get Directions
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      disabled={!selectedMarker.email}
                      onClick={() => {
                        setOutreachInitialEmails(selectedMarker.email);
                        setOutreachOpen(true);
                      }}
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Start Outreach Email
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/customer-management?customerId=${selectedMarker.id}`)}
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      View / Edit Customer
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleShowNearbyTechnicians(selectedMarker)}
                      disabled={calculatingDistances}
                      startIcon={calculatingDistances ? <CircularProgress size={14} /> : null}
                      sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      {calculatingDistances ? 'Computing...' : 'Show Nearby Technicians'}
                    </Button>

                    {!calculatingDistances && Object.keys(maidDistances).length > 0 && (() => {
                      const closestMaid = getClosestMaid();
                      return closestMaid ? (
                        <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            🎯 Closest Technician: {closestMaid.firstName} {closestMaid.lastName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            📍 {closestMaid.distance} • 🚗 {closestMaid.duration}
                          </Typography>
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                </div>
              ) : (
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Unknown marker type
                  </Typography>
                </div>
              )}
            </Box>
          </InfoWindow>
        )}
        </GoogleMap>

        {/* Distance Results Panel - positioned on map */}
        {searchedLocation && Object.keys(maidDistances).length > 0 && !selectedMarker && (
          <Paper sx={{ 
            position: 'absolute', 
            right: 10, 
            top: 10, 
            width: 320,
            maxHeight: 'calc(100vh - 40px)',
            overflow: 'hidden',
            zIndex: 10,
            display: showDistancePanel ? 'block' : 'none'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📍 Distance Results
                  {calculatingDistances && <CircularProgress size={16} />}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  From: {searchedLocation.address}
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={() => setShowDistancePanel(false)}
                sx={{ ml: 1 }}
              >
                <ExpandLess />
              </IconButton>
            </Box>
            
            <Box sx={{ maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
              <List dense>
                {sortedMaidsByDistance.map((maid, index) => {
                  const distance = maidDistances[maid.id];
                  const isClosest = index === 0 && distance;
                  
                  return (
                    <ListItem 
                      key={maid.id}
                      button
                      onClick={() => {
                        mapRef.current?.panTo(maid.location);
                        mapRef.current?.setZoom(15);
                        setSelectedMarker({...maid, type: 'maid'});
                      }}
                      sx={{ 
                        borderLeft: isClosest ? '4px solid #FFD700' : 'none',
                        bgcolor: isClosest ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: getMaidPinColor(maid.onboardingStatus),
                          width: 32, 
                          height: 32,
                          fontSize: '0.8rem'
                        }}>
                          {isClosest ? '🏆' : `${index + 1}`}
                        </Avatar>
                      </ListItemAvatar>
                      <MUIListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={isClosest ? 'bold' : 'normal'}>
                              {maid.firstName} {maid.lastName}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={maid.onboardingStatus || 'unknown'} 
                              sx={{ 
                                fontSize: '0.6rem', 
                                height: '18px',
                                bgcolor: getMaidPinColor(maid.onboardingStatus),
                                color: 'white'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              {distance && (
                                <>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    📍 {distance.distance}
                                  </Typography>
                                  <Typography variant="caption">
                                    🚗 {distance.duration}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption">
                                💰 ${maid.hourlyRate || 'N/A'}/hr
                              </Typography>
                              <Typography variant="caption">
                                ⭐ {maid.rating || 'N/R'}
                              </Typography>
                              <Typography variant="caption">
                                📞 {maid.phoneNumber || 'No phone'}
                              </Typography>
                            </Stack>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Paper>
        )}

        {/* Distance Panel Toggle Button */}
        {searchedLocation && Object.keys(maidDistances).length > 0 && !showDistancePanel && !selectedMarker && (
          <Paper sx={{ 
            position: 'absolute', 
            right: 10, 
            top: 10, 
            zIndex: 5, // Lower z-index than InfoWindow
            cursor: 'pointer'
          }}
          onClick={() => setShowDistancePanel(true)}
          >
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                📍 Distance Results ({sortedMaidsByDistance.length})
              </Typography>
              <ExpandMore />
            </Box>
          </Paper>
        )}

        {/* Alert Messages positioned on map */}
        {!loading && filteredMaids.length === 0 && filteredJobs.length === 0 && (maids.length > 0 || jobs.length > 0) && (
          <Box sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
          }}>
            <Alert severity="info">
              No items match the current filters. Try adjusting your filter settings.
            </Alert>
          </Box>
        )}

        {!loading && maids.length === 0 && jobs.length === 0 && (
          <Box sx={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
          }}>
            <Alert severity="info">
              No locations found to display. Try adjusting the filter settings above.
            </Alert>
          </Box>
        )}

        {/* Assignment Confirmation Dialog */}
        <Dialog
          open={assignmentDialogOpen}
          onClose={() => setAssignmentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Assign Technician to Job
          </DialogTitle>
          <DialogContent>
            {selectedJobForAssignment && selectedMaidForAssignment && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Job Details:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Job #:</strong> {selectedJobForAssignment.jobNumber || selectedJobForAssignment.id}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Customer:</strong> {selectedJobForAssignment.customerName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Address:</strong> {selectedJobForAssignment.extractedAddress}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Date:</strong> {selectedJobForAssignment.serviceDate}
                </Typography>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Technician to Assign:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {selectedMaidForAssignment.firstName} {selectedMaidForAssignment.lastName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Status:</strong> {selectedMaidForAssignment.onboardingStatus}
                </Typography>
                {maidDistances[selectedMaidForAssignment.id] && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Distance:</strong> {maidDistances[selectedMaidForAssignment.id].distance}
                  </Typography>
                )}

                <TextField
                  label="Technician payout"
                  type="number"
                  size="small"
                  required
                  value={assignmentPayout}
                  onChange={(e) => setAssignmentPayout(e.target.value)}
                  error={assignmentPayout !== '' && !(Number(assignmentPayout) > 0)}
                  helperText="Amount paid to this technician for the job"
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                  inputProps={{ min: 0, step: 5 }}
                  sx={{ mt: 2, width: 240 }}
                />

                {selectedJobForAssignment.maidAssignments?.allMaids?.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Currently Assigned:
                    </Typography>
                    {selectedJobForAssignment.maidAssignments.allMaids.map((maid, index) => (
                      <Typography key={index} variant="body2">
                        • {maid.firstName} {maid.lastName}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setAssignmentDialogOpen(false)}
              disabled={assigning}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAssignMaidToJob}
              variant="contained"
              disabled={assigning || !(Number(assignmentPayout) > 0)}
              startIcon={assigning ? <CircularProgress size={16} /> : null}
            >
              {assigning ? 'Assigning...' : 'Assign Technician'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Customer Outreach dialog, opened from a customer pin (prefilled) */}
      <ClientOutreachDialog
        open={outreachOpen}
        onClose={() => {
          setOutreachOpen(false);
          setOutreachInitialEmails(null);
        }}
        initialManualEmails={outreachInitialEmails}
        defaultAudienceMode="manual"
      />
    </Box>
    </AdminPageLayout>
  );
};

export default MaidMapDisplay;
